import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@repo/ui/components/ui/button";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useEffect, useState } from "react";
import { AuthFlowCard } from "../auth-flow-card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@repo/ui/components/ui/form";
import { Input } from "@repo/ui/components/ui/input";
import { cn } from "@repo/common/utils/cn";
import { AddressAutocomplete } from "./address-autocomplete";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@repo/ui/components/ui/select";
import { Textarea } from "@repo/ui/components/ui/textarea";
import { type UpdateCompanyInfoInput, useUpdateCompanyInfoMutation } from "./hooks/use-update-company-info.mutation";
import { AddressRequiredFields } from "./types";
import { AUTH_FLOW_INPUT_CLASSNAME } from "../constants";
import { isSupportedSignupCountry, SUPPORTED_COUNTRIES_NOTICE } from "./constants";
import { Loader2 } from "lucide-react";

// full API enum (graphql schema may lag; keep in sync with backend RegistrationSource)
export type RegistrationSource = "EMAIL" | "FACEBOOK" | "FREE_COURSES" | "GOOGLE" | "INSTAGRAM" | "OTHER" | "PODCAST" | "REFERRAL" | "REFERRAL_WORD_OF_MOUTH" | "SEARCH" | "SOCIAL_MEDIA" | "YOUTUBE_HOME_SERVICE_MILLIONAIRE" | "YOUTUBE_MIKE_ANDES";
const REGISTRATION_SOURCE_UI_VALUES = ["YOUTUBE_MIKE_ANDES", "YOUTUBE_HOME_SERVICE_MILLIONAIRE", "INSTAGRAM", "FACEBOOK", "EMAIL", "FREE_COURSES", "REFERRAL_WORD_OF_MOUTH", "OTHER"] as const;
type RegistrationSourceUi = (typeof REGISTRATION_SOURCE_UI_VALUES)[number];
const registrationSourceUiSchema = z.enum(REGISTRATION_SOURCE_UI_VALUES);
const REGISTRATION_SOURCE_OPTIONS: {
  value: RegistrationSourceUi;
  label: string;
}[] = [{
  value: "YOUTUBE_MIKE_ANDES",
  label: "YouTube (Mike Andes)"
}, {
  value: "YOUTUBE_HOME_SERVICE_MILLIONAIRE",
  label: "YouTube (Home Service Millionaire)"
}, {
  value: "INSTAGRAM",
  label: "Instagram"
}, {
  value: "FACEBOOK",
  label: "Facebook"
}, {
  value: "EMAIL",
  label: "Email"
}, {
  value: "FREE_COURSES",
  label: "Free Courses"
}, {
  value: "REFERRAL_WORD_OF_MOUTH",
  label: "Referral / Word of Mouth"
}, {
  value: "OTHER",
  label: "Other"
}];
export const companyFormSchema = z.object({
  businessName: z.string().min(1, "Company name is required"),
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  website: z.string().refine(val => !val || val === "" || val === "https://" || z.string().url().safeParse(val).success, "Invalid website URL").optional(),
  phoneNumber: z.string().min(1, "Phone number is required").optional(),
  address: z.string().min(1, "Address is required"),
  marketingSource: z.union([registrationSourceUiSchema, z.literal("")]).refine(val => val !== "", {
    message: "How did you hear about us is required"
  }),
  otherMarketingSource: z.string().optional(),
  sourceStimulus: z.string().min(10, "What made you sign up today is required, minimum 10 characters")
}).refine(data => {
  if (data.marketingSource === "OTHER") {
    return data.otherMarketingSource !== undefined && data.otherMarketingSource.trim().length > 0;
  }
  return true;
}, {
  message: "Please specify how you heard about us",
  path: ["otherMarketingSource"]
});
export type CompanyFormFormValues = z.input<typeof companyFormSchema>;

/** surfaced when submit runs but Places verification is missing */
export const ADDRESS_VERIFICATION_REQUIRED_MESSAGE = "Select your address from the suggestions to verify it.";

/** surfaced when the verified address is outside the supported countries */
export const UNSUPPORTED_COUNTRY_MESSAGE = "Homeworks is currently only available in the United States, Canada, and Australia.";
export function CompanyInfo() {
  const [verifiedAddress, setVerifiedAddress] = useState<AddressRequiredFields | null>(null);
  const [verifiedFormattedAddress, setVerifiedFormattedAddress] = useState<string | null>(null);
  const form = useForm<CompanyFormFormValues>({
    resolver: zodResolver(companyFormSchema),
    mode: "onChange",
    defaultValues: {
      businessName: "",
      firstName: "",
      lastName: "",
      website: "https://",
      phoneNumber: "",
      address: "",
      marketingSource: "",
      otherMarketingSource: "",
      sourceStimulus: ""
    }
  });

  // RHF can leave errors empty while isValid is false (see orgs/react-hook-form/discussions/10267).
  // Trigger validation only for touched (dirty) fields so we don't show errors on untouched fields.
  const values = form.watch();
  const valuesKey = JSON.stringify(values);
  const dirtyFieldNames = Object.keys(form.formState.dirtyFields) as (keyof CompanyFormFormValues)[];
  useEffect(() => {
    if (dirtyFieldNames.length === 0) return;
    void form.trigger(dirtyFieldNames);
  }, [form, valuesKey]); // eslint-disable-line react-hooks/exhaustive-deps -- only re-run when values change; dirtyFieldNames is read at run time

  const schemaResult = companyFormSchema.safeParse(values);
  const watchedAddress = form.watch("address");
  useEffect(() => {
    if (verifiedFormattedAddress === null) {
      return;
    }
    if (watchedAddress.trim() !== verifiedFormattedAddress) {
      setVerifiedAddress(null);
      setVerifiedFormattedAddress(null);
    }
  }, [watchedAddress, verifiedFormattedAddress]);
  const handleVerifiedAddress = (payload: AddressRequiredFields, formatted: string) => {
    form.clearErrors("address");
    const trimmed = formatted.trim();
    setVerifiedAddress(payload);
    setVerifiedFormattedAddress(trimmed);
  };
  const addressNeedsVerificationHighlight = watchedAddress.trim().length > 0 && verifiedAddress === null;
  const showAddressVerificationFormMessage = addressNeedsVerificationHighlight && !form.formState.errors?.address;
  const {
    mutate: updateCompanyInfo,
    isPending
  } = useUpdateCompanyInfoMutation();
  const handleSubmit = form.handleSubmit(() => {
    if (!verifiedAddress) {
      form.setError("address", {
        type: "manual",
        message: ADDRESS_VERIFICATION_REQUIRED_MESSAGE
      });
      return;
    }
    // The Places autocomplete is restricted to supported countries, but guard
    // against a manually entered / edge-case address slipping through.
    if (!isSupportedSignupCountry(verifiedAddress.country)) {
      form.setError("address", {
        type: "manual",
        message: UNSUPPORTED_COUNTRY_MESSAGE
      });
      return;
    }
    updateCompanyInfo({
      companyContact: {
        ...verifiedAddress,
        name: form.getValues("businessName"),
        website: form.getValues("website") !== "https://" ? form.getValues("website") : undefined,
        phone: form.getValues("phoneNumber"),
        source: form.getValues("marketingSource") as UpdateCompanyInfoInput["companyContact"]["source"],
        sourceOther: form.getValues("otherMarketingSource") ?? undefined,
        sourceStimulus: form.getValues("sourceStimulus")
      },
      userContact: {
        firstName: form.getValues("firstName"),
        lastName: form.getValues("lastName")
      }
    }, {
      onSuccess: () => {
        window.open(process.env.NEXT_PUBLIC_BASE_URL, "_parent");
      }
    });
  });
  const canProceed = schemaResult.success && !isPending;
  return <AuthFlowCard title="Tell us about your business">
      <Form {...form}>
        <form className="w-full flex flex-col gap-4" onSubmit={handleSubmit} noValidate>
          <FormField control={form.control} name={"businessName"} render={({
          field
        }) => <FormItem>
                <FormLabel weight="medium">Company name</FormLabel>
                <FormControl>
                  <Input {...field} autoComplete="organization" className={cn(form.formState.errors?.businessName ? "border-status-error" : "", AUTH_FLOW_INPUT_CLASSNAME)} sizeVariant="lg" />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <div className="grid grid-cols-2 gap-4">
            <FormField control={form.control} name={"firstName"} render={({
            field: field_0
          }) => <FormItem>
                  <FormLabel weight="medium">First name</FormLabel>
                  <FormControl>
                    <Input {...field_0} autoComplete="given-name" className={cn(form.formState.errors?.firstName ? "border-status-error" : "", AUTH_FLOW_INPUT_CLASSNAME)} sizeVariant="lg" />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            <FormField control={form.control} name={"lastName"} render={({
            field: field_1
          }) => <FormItem>
                  <FormLabel weight="medium">Last name</FormLabel>
                  <FormControl>
                    <Input {...field_1} autoComplete="family-name" className={cn(form.formState.errors?.lastName ? "border-status-error" : "", AUTH_FLOW_INPUT_CLASSNAME)} sizeVariant="lg" />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />
          </div>

          <FormField control={form.control} name={"website"} render={({
          field: field_2
        }) => <FormItem>
                <FormLabel weight="medium">Website</FormLabel>
                <FormControl>
                  <Input {...field_2} autoComplete="url" placeholder="https://www.example.com" className={cn(form.formState.errors?.website ? "border-status-error" : "", AUTH_FLOW_INPUT_CLASSNAME)} sizeVariant="lg" />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={form.control} name={"phoneNumber"} render={({
          field: field_3
        }) => <FormItem>
                <FormLabel weight="medium">Mobile phone</FormLabel>
                <FormControl>
                  <Input {...field_3} autoComplete="tel" className={cn(form.formState.errors?.phoneNumber ? "border-status-error" : "", AUTH_FLOW_INPUT_CLASSNAME)} sizeVariant="lg" />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={form.control} name={"address"} render={({
          field: field_4
        }) => <FormItem>
                <FormLabel weight="medium">Address</FormLabel>
                <FormControl>
                  <AddressAutocomplete value={field_4.value} onChange={field_4.onChange} onAddressConfirm={handleVerifiedAddress} placeholder="Start typing your address..." className={cn(form.formState.errors?.address ? "border-status-error" : addressNeedsVerificationHighlight ? "border-status-warning-lighten-1 bg-status-warning-lighten-3" : "", AUTH_FLOW_INPUT_CLASSNAME)} sizeVariant="lg" />
                </FormControl>
                <FormMessage className={showAddressVerificationFormMessage ? "text-status-warning-darken-1" : undefined}>
                  {showAddressVerificationFormMessage ? ADDRESS_VERIFICATION_REQUIRED_MESSAGE : null}
                </FormMessage>
                <p className="text-xs text-foreground-muted">
                  {SUPPORTED_COUNTRIES_NOTICE}
                </p>
              </FormItem>} />

          <FormField control={form.control} name={"marketingSource"} render={({
          field: field_5
        }) => <FormItem>
                <FormLabel weight="medium">
                  How did you hear about us?
                </FormLabel>
                <FormControl>
                  <Select onValueChange={field_5.onChange} defaultValue={field_5.value}>
                    <FormControl>
                      <SelectTrigger className={cn(form.formState.errors?.marketingSource ? "border-status-error" : "", AUTH_FLOW_INPUT_CLASSNAME)} size="lg">
                        <SelectValue placeholder="Select an option" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {REGISTRATION_SOURCE_OPTIONS.map(source => <SelectItem key={source.value} value={source.value}>
                          {source.label}
                        </SelectItem>)}
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormMessage />
              </FormItem>} />

          {form.watch("marketingSource") === "OTHER" && <FormField control={form.control} name={"otherMarketingSource"} render={({
          field: field_6
        }) => <FormItem>
                  <FormLabel weight="medium">Other</FormLabel>
                  <FormControl>
                    <Textarea {...field_6} autoComplete="off" data-1p-ignore placeholder="Please specify..." className={cn("h-12", form.formState.errors?.otherMarketingSource ? "border-status-error" : "", AUTH_FLOW_INPUT_CLASSNAME)} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />}

          <FormField control={form.control} name={"sourceStimulus"} render={({
          field: field_7
        }) => <FormItem>
                <FormLabel weight="medium">
                  What made you sign up today?
                </FormLabel>
                <FormControl>
                  <Input {...field_7} autoComplete="off" data-1p-ignore placeholder="A specific video, post, or new software feature." name="signup-company-source-stimulus" className={cn(form.formState.errors?.sourceStimulus ? "border-status-error" : "", AUTH_FLOW_INPUT_CLASSNAME)} sizeVariant="lg" />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <div className="mt-8">
            <Button type="submit" disabled={!canProceed} className="w-full text-surface bg-brand hover:bg-brand-hover h-12">
              {isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Go to dashboard"}
            </Button>
          </div>
        </form>
      </Form>
    </AuthFlowCard>;
}